import { useAuthStore } from "@/store/auth.store"
import { getAnonymousFingerprint } from "@/lib/copilot/fingerprint"
import type {
  ApiEnvelope,
  ChatFile,
  ChatToolDefinition,
  Conversation,
  ConversationList,
  CopilotSuggestion,
  FeedbackRating,
  Message,
  MessageList,
  PageMeta,
  Run,
  RunAction,
  SendMessageResult,
} from "./chat.types"

export const CHAT_API_BASE = `${import.meta.env.VITE_API_URL ?? "http://localhost:7590"}/v1`

/** Model hint that tells the backend to use its configured default model. */
export const DEFAULT_MODEL_HINT = "default"

export class ChatError extends Error {
  public status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = "ChatError"
    this.status = status
  }
}

/** Shown when the backend returns 429. Never auto-retry — that defeats the rate limiter. */
export const RATE_LIMIT_MESSAGE = "Too many requests, please try again in a moment."
/** Guest variant: signing up moves the user to far higher buckets, so lead with it. */
export const RATE_LIMIT_GUEST_MESSAGE =
  "You've hit a temporary limit. Slow down for a moment, or create an account to keep chatting."

interface RequestInitCompat {
  method?: string
  headers?: Record<string, string>
  body?: BodyInit | null
  signal?: AbortSignal
}

async function chatRequest<T>(path: string, init: RequestInitCompat = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  if (token) headers["Authorization"] = `Bearer ${token}`
  else {
    // Anonymous visitors identify via a stable fingerprint so the backend can
    // rate-limit guest chat sessions.
    const fingerprint = getAnonymousFingerprint()
    if (fingerprint) headers["X-Anonymous-Fingerprint"] = fingerprint
  }

  const res = await fetch(`${CHAT_API_BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null
    throw new ChatError(
      res.status,
      (body && (body.message || body.error)) || `Request failed with status ${res.status}`
    )
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Wraps the raw backend envelope (`{ success, statusCode, message, data, meta }`). */
async function envelope<T, M = unknown>(path: string, init?: RequestInitCompat): Promise<ApiEnvelope<T, M>> {
  return chatRequest<ApiEnvelope<T, M>>(path, init)
}

const EMPTY_META: PageMeta = { total: 0, page: 1, limit: 20, totalPages: 0 }

function toQuery(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") qs.set(key, String(value))
  }
  const s = qs.toString()
  return s ? `?${s}` : ""
}

// ── Conversations ───────────────────────────────────────────────────────────────
export async function listConversations(
  params: { page?: number; limit?: number } = {}
): Promise<ConversationList> {
  const env = await envelope<Conversation[], PageMeta>(`/conversations${toQuery(params)}`)
  return { data: env.data ?? [], meta: env.meta ?? { ...EMPTY_META, limit: params.limit ?? 20 } }
}

export async function createConversation(title?: string): Promise<Conversation> {
  const env = await envelope<Conversation>(`/conversations`, {
    method: "POST",
    body: JSON.stringify(title ? { title } : {}),
  })
  return env.data
}

export async function renameConversation(id: string, title: string): Promise<Conversation> {
  const env = await envelope<Conversation>(`/conversations/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  })
  return env.data
}

export async function deleteConversation(id: string): Promise<void> {
  await envelope<unknown>(`/conversations/${id}`, { method: "DELETE" })
}

// ── Anonymous → signed-in handoff ──────────────────────────────────────────────
export interface ClaimAnonymousConversationsResult {
  claimed: number
}

/**
 * Move conversations created anonymously under `anonymousId` onto the signed-in
 * user. Bearer-only (guests get 401) — never call it from the guest branch.
 */
export async function claimAnonymousConversations(
  anonymousId: string
): Promise<ClaimAnonymousConversationsResult> {
  const env = await envelope<ClaimAnonymousConversationsResult>(`/conversations/claim`, {
    method: "POST",
    body: JSON.stringify({ anonymousId }),
  })
  return env.data
}

// ── Messages ────────────────────────────────────────────────────────────────────
export async function listMessages(
  conversationId: string,
  params: { page?: number; limit?: number } = {}
): Promise<MessageList> {
  const env = await envelope<Message[], PageMeta>(
    `/conversations/${conversationId}/messages${toQuery(params)}`
  )
  return { data: env.data ?? [], meta: env.meta ?? { ...EMPTY_META, limit: params.limit ?? 100 } }
}

export interface SendMessageBody {
  content: string
  model?: string
  stream?: boolean
  tools?: ChatToolDefinition[]
}

export async function sendMessage(
  conversationId: string,
  body: SendMessageBody
): Promise<SendMessageResult> {
  const env = await envelope<SendMessageResult>(`/conversations/${conversationId}/messages`, {
    method: "POST",
    body: JSON.stringify({
      ...body,
      model: body.model ?? DEFAULT_MODEL_HINT,
      stream: body.stream ?? true,
    }),
  })
  return env.data
}

export interface GetCopilotSuggestionsBody {
  path?: string
  query?: Record<string, string>
  role?: string
  recentSearches?: string[]
}

/**
 * Asks the assistant backend to generate context-aware quick-reply suggestions
 * for the floating widget, based on the current page, role and recent searches.
 * Returns an empty array when generation fails so callers can fall back to the
 * static per-page prompts.
 */
export async function getCopilotSuggestions(body: GetCopilotSuggestionsBody): Promise<CopilotSuggestion[]> {
  const env = await envelope<{ suggestions?: CopilotSuggestion[] }>(`/assistant/suggestions`, {
    method: "POST",
    body: JSON.stringify(body),
  })
  return env.data?.suggestions ?? []
}

export async function regenerateMessage(
  conversationId: string,
  messageId: string,
  body?: { model?: string; stream?: boolean; tools?: ChatToolDefinition[] }
): Promise<SendMessageResult> {
  const env = await envelope<SendMessageResult>(
    `/conversations/${conversationId}/messages/${messageId}/regenerate`,
    { method: "POST", body: JSON.stringify({ ...body, model: body?.model ?? DEFAULT_MODEL_HINT }) }
  )
  return env.data
}

// ── Runs ────────────────────────────────────────────────────────────────────────
export async function getRun(runId: string): Promise<Run> {
  const env = await envelope<{ run: Run }>(`/runs/${runId}`)
  return env.data.run
}

export async function cancelRun(runId: string): Promise<void> {
  await envelope<unknown>(`/runs/${runId}/cancel`, { method: "POST" })
}

export async function listRunActions(runId: string): Promise<RunAction[]> {
  const env = await envelope<RunAction[]>(`/runs/${runId}/actions`)
  return env.data ?? []
}

export async function approveAction(runId: string, actionId: string): Promise<void> {
  await envelope<unknown>(`/runs/${runId}/actions/${actionId}/approve`, { method: "POST" })
}

export async function rejectAction(runId: string, actionId: string): Promise<void> {
  await envelope<unknown>(`/runs/${runId}/actions/${actionId}/reject`, { method: "POST" })
}

export interface SubmitToolResultResult {
  accepted: boolean
}

/**
 * Submits the real result of a frontend-executed tool call (e.g.
 * `get_page_context`) so the backend can feed it back to the LLM and resume the
 * run. Returns `accepted: false` when the run already moved on (e.g. timed out).
 */
export async function submitToolResult(
  runId: string,
  actionId: string,
  result: string
): Promise<SubmitToolResultResult> {
  const env = await envelope<SubmitToolResultResult>(`/runs/${runId}/actions/${actionId}/result`, {
    method: "POST",
    body: JSON.stringify({ result }),
  })
  return env.data
}

// ── Files ───────────────────────────────────────────────────────────────────────
export async function uploadChatFile(file: File, purpose?: string, caption?: string): Promise<ChatFile> {
  const token = useAuthStore.getState().accessToken
  const fd = new FormData()
  fd.append("file", file)
  if (purpose) fd.append("purpose", purpose)
  if (caption) fd.append("caption", caption)

  const res = await fetch(`${CHAT_API_BASE}/files`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd, // no manual Content-Type — the browser sets the boundary
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null
    throw new ChatError(
      res.status,
      (body && (body.message || body.error)) || `Upload failed with status ${res.status}`
    )
  }

  const env = (await res.json()) as ApiEnvelope<ChatFile>
  return env.data ?? (env as unknown as ChatFile)
}

export async function getChatFile(id: string): Promise<ChatFile> {
  const env = await envelope<ChatFile>(`/files/${id}`)
  return env.data
}

export async function deleteChatFile(id: string): Promise<void> {
  await envelope<unknown>(`/files/${id}`, { method: "DELETE" })
}

// ── Feedback ────────────────────────────────────────────────────────────────────
export async function sendFeedback(
  messageId: string,
  rating: FeedbackRating,
  comment?: string
): Promise<void> {
  await envelope<unknown>(`/messages/${messageId}/feedback`, {
    method: "POST",
    body: JSON.stringify(comment ? { rating, comment } : { rating }),
  })
}